import styled, { keyframes } from 'styled-components';

import { ActionStatus, WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import CircleCheckIcon from 'svg/CircleCheckIcon';
import CircleMinusIcon from 'svg/CircleMinusIcon';

import { completeWithParked } from './referralProgress';

// `denied` (OLIO-3170) is the one indicator no action status produces: it is set by the rail from
// the referral's recorded outcome, on the two categories a denial concludes (Facility Decision(s)
// and Referral Outcome) — see ProgressRail.buildSteps. Red like `error`, but a bar rather than a
// "!": nothing is broken, the answer was no.
export type StatusIndicator =
  | 'complete'
  | 'running'
  | 'review'
  | 'attention'
  | 'error'
  | 'pending'
  | 'muted'
  | 'denied';

// One action's visual state, keyed off the API's `effectiveStatus` (the presentation status the
// kind derived — e.g. a completed DenialCheck match arrives as `flagged`). `review`
// (action_required) is ready for a person; `attention` means blocked or a flagged result.
// "At rest" (canceled/skipped) is a lifecycle fact, so it reads engine `status` — matching how
// categoryStatus/completeWithParked encode terminal states, not the presentation axis.
export function statusIndicator(action: WorkflowAction): StatusIndicator {
  if (action.status === ActionStatus.CANCELED || action.status === ActionStatus.SKIPPED) {
    return 'muted';
  }
  switch (action.effectiveStatus) {
    case ActionStatus.COMPLETED:
      return 'complete';
    case ActionStatus.RUNNING:
      return 'running';
    case ActionStatus.ACTION_REQUIRED:
      return 'review';
    case ActionStatus.FLAGGED:
      return 'attention';
    case ActionStatus.ERRORED:
      return 'error';
    default:
      return 'pending';
  }
}

// A category's roll-up (design §12): an errored action wins (a failure must surface even when
// something else needs attention), then blocked/changed, then ready-for-review, then
// done-when-all-done, then still-running, else waiting. Keyed off effectiveStatus so a flagged
// result (e.g. a DenialCheck match) rolls the category up to attention. Priority order MUST match
// categoryStatus (referralProgress.ts) so the rail icon and the card badge can't disagree.
export function rollupIndicator(actions: WorkflowAction[]): StatusIndicator {
  if (actions.some((a) => a.effectiveStatus === ActionStatus.ERRORED)) return 'error';
  if (actions.some((a) => a.effectiveStatus === ActionStatus.FLAGGED)) return 'attention';
  if (actions.some((a) => a.effectiveStatus === ActionStatus.ACTION_REQUIRED)) return 'review';
  if (completeWithParked(actions)) return 'complete';
  if (actions.some((a) => a.effectiveStatus === ActionStatus.RUNNING)) return 'running';
  if (
    actions.length > 0 &&
    actions.every((a) => a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)
  )
    return 'muted';
  return 'pending';
}

// The at-rest word's color. Exported because the rail styles its own labels rather than going
// through indicatorColor (it colors ONLY this state — every other rail label stays black100 and
// tells state apart by weight), and the card rows and the rail showing the same step in two
// different grays is the exact divergence OLIO-3270 set out to remove.
export const AT_REST_TEXT = colors.black50;

// The accent color a title/text takes for a given state (matches the icon).
export function indicatorColor(indicator: StatusIndicator): string | undefined {
  if (indicator === 'running') return colors.purple100;
  if (indicator === 'review') return colors.blue100;
  if (indicator === 'attention') return colors.orangeHover; // the warning-tone text color (see StatusBadge)
  if (indicator === 'error' || indicator === 'denied') return colors.red100;
  // At rest (Not Applicable / Canceled) — the word recedes to match its icon instead of sitting at
  // full-strength body color (OLIO-3270).
  if (indicator === 'muted') return AT_REST_TEXT;
  return undefined;
}

// A row's title/name is bold while it's actively running, needs attention (flagged), or is
// ready for review — everything else (complete, error, pending, muted) reads at normal weight so
// the in-flight/blocked/actionable steps are what visually stand out.
export function isEmphasized(indicator: StatusIndicator): boolean {
  return indicator === 'running' || indicator === 'attention' || indicator === 'review';
}

export default function StatusIcon({ indicator, size = 18 }: { indicator: StatusIndicator; size?: number }) {
  if (indicator === 'complete') return <CircleCheckIcon width={size} height={size} fill={colors.green100} />;
  if (indicator === 'denied')
    return <CircleMinusIcon width={size} height={size} fill={colors.red100} role='img' aria-label='Denied' />;
  if (indicator === 'running') return <Spinner $size={size} role='img' aria-label='Running' />;
  if (indicator === 'review')
    return (
      <ReviewRing $size={size} role='img' aria-label='Ready for review'>
        <svg width={size * 0.58} height={size * 0.58} viewBox='0 0 12 9' fill='none'>
          <path
            d='M1 4.6 L4.2 7.6 L11 1'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </ReviewRing>
    );
  if (indicator === 'attention')
    return (
      <Glyph $size={size} $bg={colors.orange100}>
        !
      </Glyph>
    );
  if (indicator === 'error')
    return (
      <Glyph $size={size} $bg={colors.red100}>
        !
      </Glyph>
    );
  // At rest: a solid gray dot with no border. The bordered ring reads as an *empty* step still
  // waiting its turn, which is exactly what Pending is and Not Applicable/Canceled are not; the
  // filled dot also lines the rail's at-rest steps up with the other filled glyphs (OLIO-3270).
  if (indicator === 'muted') return <AtRest $size={size} />;
  return <Hollow $size={size} />;
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span<{ $size: number }>`
  display: inline-block;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 2px solid ${colors.purple25};
  border-top-color: ${colors.purple100};
  animation: ${spin} 0.8s linear infinite;
`;

const ReviewRing = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 1.5px solid ${colors.blue100};
  color: ${colors.blue100};
`;

const Glyph = styled.span<{ $size: number; $bg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: #fff;
  font-size: ${({ $size }) => Math.round($size * 0.66)}px;
  font-weight: 500;
  line-height: 1;
`;

const Hollow = styled.span<{ $size: number }>`
  display: inline-block;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 1.5px solid ${colors.black25};
`;

// black10, not the black5 the neutral pill uses: a pill is a filled shape wide enough to read at
// 5%, but a 12-18px dot at that opacity all but disappeared against the card. Shared by the card
// rows and both sizes of rail circle, so the at-rest step looks the same in either place.
const AtRest = styled.span<{ $size: number }>`
  display: inline-block;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${colors.black10};
`;
